/**
 * Course Mutation Hooks
 *
 * TanStack Query mutations for course-related actions.
 *
 * The attachment write hooks target the learner-facing `/courses/...` routes
 * (BE contract `api-course-attachments.md`), not `/studio/courses/...` — the
 * Studio Resources tab is a consumer of those, so they live here beside
 * `useCourseAttachments` rather than in `studio-courses.mutations.ts`.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { coursesClient, type SaveProgressPayload } from '../client/courses.client';
import { queryKeys } from '../query-keys';
import { captureError } from '@/shared/lib/errors/capture';
import type {
  UpdateCourseAttachmentPayload,
  UploadCourseAttachmentPayload,
} from '@/types/course-attachment';

/**
 * Save video watch progress for a course lesson.
 * Fire-and-forget: errors are captured silently, no UI feedback.
 */
export function useSaveCourseProgress() {
  return useMutation({
    mutationFn: ({ videoUuid, ...payload }: SaveProgressPayload & { videoUuid: string }) =>
      coursesClient.saveProgress(videoUuid, payload),
    onError: (error) => {
      captureError(error, { component: 'useSaveCourseProgress', action: 'saveProgress' });
    },
  });
}

export function useUploadCourseAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      courseUuid,
      payload,
    }: {
      courseUuid: string;
      payload: UploadCourseAttachmentPayload;
    }) => coursesClient.uploadAttachment(courseUuid, payload),
    onSuccess: (_data, { courseUuid }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.courses.attachments(courseUuid) });
    },
  });
}

/**
 * PATCH is media-scoped server-side, so `courseUuid` rides along purely to
 * invalidate the right listing. Refetching also re-signs every `download_url`.
 */
export function useUpdateCourseAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      mediaUuid,
      payload,
    }: {
      courseUuid: string;
      mediaUuid: string;
      payload: UpdateCourseAttachmentPayload;
    }) => coursesClient.updateAttachment(mediaUuid, payload),
    onSuccess: (_data, { courseUuid }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.courses.attachments(courseUuid) });
    },
  });
}

export function useDeleteCourseAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ mediaUuid }: { courseUuid: string; mediaUuid: string }) =>
      coursesClient.deleteAttachment(mediaUuid),
    onSuccess: (_data, { courseUuid }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.courses.attachments(courseUuid) });
    },
  });
}
